import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TRANSLATION_KEYS } from "./keys";
import { fetchLanguageBundle, fetchPublishedLanguages } from "./api";

/* Runtime side of the translation system (see keys.js for the registry and
   backend/i18n_admin.py for the data model).

   t(key, vars) resolves   bundle[key]  ->  English from TRANSLATION_KEYS
   and never returns a raw key or a blank for a registered key: a missing or
   empty translation shows the English text. The bundle fetch is public and
   re-read on every language change, so publishing a language in the admin
   panel takes effect for users without a deploy.

   The chosen language is remembered in localStorage (like the app's other
   per-browser prefs). The last bundle per language is cached there too so a
   repeat visit paints translated straight away instead of flashing English
   first; it is refreshed from the server in the background. */

const LANG_KEY = "hwh_lang";
const bundleKey = (code) => `hwh_i18n_bundle_${code}`;

const store = {
  get(k) { try { return window.localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { window.localStorage.setItem(k, v); } catch { /* private mode etc. */ } },
  del(k) { try { window.localStorage.removeItem(k); } catch { /* ignore */ } },
};

const readCachedBundle = (code) => {
  try { return JSON.parse(store.get(bundleKey(code)) || "null") || {}; } catch { return {}; }
};

const interpolate = (text, vars) =>
  vars ? text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined || vars[k] === null ? m : String(vars[k]))) : text;

/* Plurals: call t(key, { count: n, ... }). If the registry has key + "One" /
   key + "Other" (and optionally Zero/Two/Few/Many) the variant matching the
   language's own plural rule is used, so "1 ayah" / "3 ayahs" -- and languages
   with other plural forms -- read correctly. A key that has no variants is used
   as-is, so a plain {count} placeholder still works. */
const PLURAL_SUFFIX = { zero: "Zero", one: "One", two: "Two", few: "Few", many: "Many", other: "Other" };
function pluralKey(key, vars, lang, has) {
  if (!vars || typeof vars.count !== "number") return key;
  let cat = "other";
  try { cat = new Intl.PluralRules(lang).select(vars.count); } catch { /* unknown locale: English rule */ cat = vars.count === 1 ? "one" : "other"; }
  for (const k of [key + PLURAL_SUFFIX[cat], key + "Other"]) if (has(k)) return k;
  return key;
}

/* tx("English text"): translate a label that lives in a constant (a status name,
   a button list...) by looking its text up in the registry. Text that isn't in the
   registry -- user content, server data -- comes back unchanged, so it is safe to
   wrap anything that might be either. */
let _byText = null;
const keyForText = (text) => {
  if (!_byText) {
    _byText = {};
    for (const [k, v] of Object.entries(TRANSLATION_KEYS)) if (!(v in _byText)) _byText[v] = k;
  }
  return _byText[text];
};

const Ctx = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => store.get(LANG_KEY) || "en");
  const [bundle, setBundle] = useState(() => {
    const saved = store.get(LANG_KEY);
    return saved && saved !== "en" ? readCachedBundle(saved) : {};
  });
  const [languages, setLanguages] = useState([]);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);

  const setLang = useCallback((code) => {
    const next = code || "en";
    if (next === "en") { store.del(LANG_KEY); setBundle({}); } else { store.set(LANG_KEY, next); setBundle(readCachedBundle(next)); }
    setLangState(next);
  }, []);

  // Published languages for the switcher. If the saved language has since
  // been unpublished, drop back to English (only once the list really loaded).
  useEffect(() => {
    let cancelled = false;
    fetchPublishedLanguages()
      .then((list) => {
        if (cancelled) return;
        setLanguages(list);
        setLanguagesLoaded(true);
      })
      .catch(() => { /* offline: keep whatever is cached, hide the switcher */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (languagesLoaded && lang !== "en" && !languages.some((l) => l.code === lang)) setLang("en");
  }, [languagesLoaded, languages, lang, setLang]);

  // Bundle for the active language, refreshed on every change.
  useEffect(() => {
    document.documentElement.lang = lang;
    if (lang === "en") return undefined;
    let cancelled = false;
    fetchLanguageBundle(lang)
      .then((b) => {
        if (cancelled) return;
        setBundle(b);
        store.set(bundleKey(lang), JSON.stringify(b));
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setLang("en"); // unpublished / removed
        // any other error (offline etc.): keep the cached bundle, else English
      });
    return () => { cancelled = true; };
  }, [lang, setLang]);

  const t = useCallback((origKey, vars) => {
    const has = (k) => k in TRANSLATION_KEYS || (lang !== "en" && typeof bundle[k] === "string");
    const key = pluralKey(origKey, vars, lang, has);
    const fromBundle = lang !== "en" ? bundle[key] : undefined;
    const text = typeof fromBundle === "string" && fromBundle.trim() ? fromBundle : TRANSLATION_KEYS[key];
    if (typeof text !== "string") {
      if (process.env.NODE_ENV !== "production") console.warn(`[i18n] unknown key "${key}" -- add it to src/i18n/keys.js`);
      return "";
    }
    return interpolate(text, vars);
  }, [lang, bundle]);

  const tx = useCallback((text, vars) => {
    const key = typeof text === "string" ? keyForText(text) : undefined;
    return key ? t(key, vars) : text;
  }, [t]);

  const value = useMemo(() => {
    const locale = lang === "en" ? undefined : lang;
    return {
      lang,
      setLang,
      languages,
      t,
      tx,
      // Locale-aware formatting for the dynamic values that must NOT go into
      // translation keys. Intl throws on a locale it doesn't know -> fall back.
      fmtNumber: (n, opts) => { try { return new Intl.NumberFormat(locale, opts).format(n); } catch { return String(n); } },
      fmtDate: (d, opts) => {
        try { return new Intl.DateTimeFormat(locale, opts || { dateStyle: "medium" }).format(new Date(d)); } catch { return String(d); }
      },
    };
  }, [lang, setLang, languages, t, tx]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// Outside a provider (a unit test, an isolated render) everything still works in English.
const FALLBACK = {
  lang: "en", setLang: () => {}, languages: [],
  t: (key, vars) => interpolate(TRANSLATION_KEYS[pluralKey(key, vars, "en", (k) => k in TRANSLATION_KEYS)] ?? "", vars),
  tx: (text) => text,
  fmtNumber: (n) => String(n), fmtDate: (d) => String(d),
};

export const useLanguage = () => useContext(Ctx) || FALLBACK;
export const useT = () => useLanguage().t;
